import { useGetPredictionMarketMyEntries } from "@/hooks/predictionMarket/useGetPredictionMarketMyEntries";
import { useHandleError } from "@/hooks/useHandleError";
import type { PredictionChoice, PredictionMarketMyEntry } from "@/types/PredictionMarket";
import { ClockIcon } from "@roman/shared/components/Icons";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { cn } from "@roman/shared/utils";
import { format } from "date-fns";

export const MyPredictionMarketEntryPage = () => {
  const { myEntries, isLoading } = useGetPredictionMarketMyEntries({
    swrOptions: {
      onError: useHandleError("予想市場のエントリーの取得に失敗しました"),
    },
  });

  if (isLoading || !myEntries.length) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex flex-col gap-3 p-2">
      <h1 className="text-lg font-bold">あなたのベット</h1>
      <div className="flex flex-col gap-2">
        {myEntries.map((entry) => (
          <MyEntryCard key={entry.id} predictionMarketMyEntry={entry} />
        ))}
      </div>
    </div>
  );
};

const MyEntryCard = ({ predictionMarketMyEntry }: { predictionMarketMyEntry: PredictionMarketMyEntry }) => {
  return (
    <div className="p-3 bg-white rounded-xl">
      <div className="flex justify-between">
        <div className="py-0.5 px-1.5 text-xs text-dark-gray border rounded-full">{predictionMarketMyEntry.label}</div>
        <div className="flex gap-1 text-dark-gray text-xs items-center">
          <ClockIcon className="h-4 w-4" />
          <span>{format(new Date(predictionMarketMyEntry.entryCloseTime), "yyyy年M月d日 HH:mm")}</span>
        </div>
      </div>
      <p className="mt-3 text-black text-lg font-bold">{predictionMarketMyEntry.title}</p>

      <PredictionChoiceStatesComponent choices={predictionMarketMyEntry.choices} />

      <div className="border border-[#90E9FF] rounded-md overflow-hidden flex divide-x divide-[#90E9FF] mt-3">
        <div className="w-1/2 flex flex-col items-center">
          <div className="py-1.5 text-center bg-light-blue text-xs text-dark-gray w-full">あなたのベット額</div>
          <div className="flex-1 flex text-black font-bold text-xl text-center items-center py-2">
            {Number(predictionMarketMyEntry.freePoints).toLocaleString()} pt
          </div>
        </div>

        <div className="w-1/2 flex flex-col items-center">
          <div className="py-1.5 text-center bg-light-blue text-xs text-dark-gray w-full">予想獲得</div>
          <div className="flex-1 flex text-primary font-bold text-xl text-center items-center py-2">
            {Math.round(Number(predictionMarketMyEntry.potentialFreePoints)).toLocaleString()} pt
          </div>
        </div>
      </div>

      {/* Skip this for now */}
      {/* <div className="mt-2.5 flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-black font-bold text-sm">
          <ThumbaCoin className="w-3 h-3" />
          {(180000).toLocaleString()} pt
        </div>
      </div> */}
    </div>
  );
};

const PredictionChoiceStatesComponent = ({ choices }: { choices: PredictionChoice[] }) => {
  // Define some sample colors for the options
  const colors = [
    { dot: "primary", background: "#EDF9FF" },
    // { dot: "green", background: "#DEFFE8" },
    // { dot: "red", background: "#FFE5E5" },
    // { dot: "blue", background: "#E0F2FF" },
    // { dot: "orange", background: "#FFF0E5" },
  ];

  return (
    <div className="flex flex-col gap-2 w-full mt-3">
      {choices.map((choice, idx) => {
        const color = colors[idx % colors.length]; // Cycle through colors
        const expectedValue = (1 / choice.probability).toFixed(2);

        return (
          <div
            key={idx}
            className="relative flex items-center justify-between p-3 rounded-md w-full text-sm"
            style={{ backgroundColor: color.background }}
          >
            <div className="flex items-center gap-2 w-full overflow-hidden">
              <span className={cn("w-3 h-3 rounded-full", `bg-${color.dot}`)} />
              <span className="truncate font-bold text-gray-800">{choice.name}</span>
            </div>
            <span className="font-bold text-gray-800 ml-2 shrink-0">{(choice.probability * 100).toFixed(2)}%</span>

            <span className="absolute -top-2.5 right-3 text-dark-gray text-[10px]">
              期待値: x <span className="font-bold">{expectedValue}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};
